/**
 * DealEasy Price Display Modifier
 * Intercepts DealEasy price updates and converts to unit price
 */

(function() {
  'use strict';

  // Store original textContent setters
  const originalSetters = new WeakMap();
  
  // Function to convert price to unit price
  function convertToUnitPrice(priceElement, totalPriceText, quantity) {
    if (!totalPriceText || !quantity || quantity < 1) {
      return totalPriceText; // Return original if can't convert
    }
    
    // Extract numeric value from price string
    const priceMatch = totalPriceText.match(/[\d,]+\.?\d*/);
    if (!priceMatch) {
      return totalPriceText;
    }

    // Remove commas and convert to number
    const totalPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
    
    if (isNaN(totalPrice) || totalPrice <= 0) {
      return totalPriceText;
    }
    
    // Calculate unit price
    const unitPrice = totalPrice / quantity;

    if (isNaN(unitPrice) || unitPrice <= 0) {
      return totalPriceText;
    }

    // Format unit price (preserve original format)
    let formattedUnitPrice;
    if (totalPriceText.includes('$')) {
      formattedUnitPrice = '$' + unitPrice.toFixed(2);
    } else if (totalPriceText.includes('€')) {
      formattedUnitPrice = '€' + unitPrice.toFixed(2);
    } else if (totalPriceText.includes('£')) {
      formattedUnitPrice = '£' + unitPrice.toFixed(2);
    } else {
      formattedUnitPrice = unitPrice.toFixed(2);
    }

    return formattedUnitPrice;
  }

  // Function to get quantity for a price element
  function getQuantityForPrice(priceElement) {
    const priceInfoWrapper = priceElement.closest('.lb-qty-selector-price-info-wrapper');
    if (!priceInfoWrapper) {
      return 1;
    }
    
    const discountInfoWrapper = priceInfoWrapper.closest('.lb-qty-selector-discount-info-wrapper');
    if (!discountInfoWrapper) {
      return 1;
    }
    
    const quantityTextElement = discountInfoWrapper.querySelector('.lb-qty-selector-quantity-text');
    if (!quantityTextElement) {
      return 1;
    }
    
    const quantityText = quantityTextElement.textContent.trim();
    const quantityMatch = quantityText.match(/\d+/);
    
    return quantityMatch ? parseInt(quantityMatch[0]) : 1;
  }

  // Function to intercept price element updates
  function interceptPriceElement(priceElement) {
    // Skip if already intercepted
    if (priceElement.dataset.unitPriceIntercepted === 'true') {
      return;
    }
    
    // Mark as intercepted
    priceElement.dataset.unitPriceIntercepted = 'true';
    
    // Store original textContent descriptor
    const descriptor = Object.getOwnPropertyDescriptor(Node.prototype, 'textContent') || 
                       Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'textContent');
    
    if (!descriptor) {
      return;
    }
    
    // Create custom setter
    const customSetter = function(value) {
      // Check if this is a price element we care about
      if (this.classList.contains('money') && 
          this.closest('.lb-qty-selector-discounted-price-text')) {
        
        const priceText = String(value).trim();
        
        // Check if it looks like a total price (not already a unit price)
        const storedOriginal = this.dataset.originalTotal;
        let shouldConvert = true;
        
        if (storedOriginal) {
          const currentNum = parseFloat(priceText.replace(/[^\d.]/g, ''));
          const storedNum = parseFloat(storedOriginal.replace(/[^\d.]/g, ''));
          
          // If current is much smaller than stored, it's already a unit price
          if (currentNum < storedNum * 0.8) {
            shouldConvert = false;
            // Use stored original for conversion
            value = storedOriginal;
          } else {
            // It's a new total price - update stored
            this.dataset.originalTotal = priceText;
          }
        } else {
          // First time - store as original
          this.dataset.originalTotal = priceText;
        }
        
        if (shouldConvert) {
          const quantity = getQuantityForPrice(this);
          const unitPrice = convertToUnitPrice(this, value, quantity);
          
          // Call original setter with unit price
          if (descriptor.set) {
            descriptor.set.call(this, unitPrice);
          } else {
            this.textContent = unitPrice;
          }
          return;
        }
      }
      
      // For non-price elements or already converted prices, use original setter
      if (descriptor.set) {
        descriptor.set.call(this, value);
      } else {
        this.textContent = value;
      }
    };
    
    // Override textContent for this element
    Object.defineProperty(priceElement, 'textContent', {
      get: function() {
        return descriptor.get ? descriptor.get.call(this) : this.textContent;
      },
      set: customSetter,
      configurable: true
    });
  }

  // Function to find and intercept all price elements
  function interceptAllPrices() {
    const priceElements = document.querySelectorAll('.lb-qty-selector-discounted-price-text .money');
    priceElements.forEach(interceptPriceElement);
  }

  // Function to watch for new price elements
  function watchForNewPrices() {
    const observer = new MutationObserver(function(mutations) {
      let foundNew = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              // Check if it's a price element or contains one
              if (node.classList && node.classList.contains('money') && 
                  node.closest('.lb-qty-selector-discounted-price-text')) {
                interceptPriceElement(node);
                foundNew = true;
              } else if (node.querySelector) {
                const priceEl = node.querySelector('.lb-qty-selector-discounted-price-text .money');
                if (priceEl) {
                  interceptPriceElement(priceEl);
                  foundNew = true;
                }
              }
            }
          });
        }
      });
      
      if (foundNew) {
        // Also check all prices again in case we missed any
        setTimeout(interceptAllPrices, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize
  function init() {
    // Intercept existing prices
    interceptAllPrices();
    
    // Watch for new prices
    watchForNewPrices();
    
    // Retry after delays
    setTimeout(interceptAllPrices, 500);
    setTimeout(interceptAllPrices, 1500);
    setTimeout(interceptAllPrices, 3000);
    
    window.addEventListener('load', function() {
      setTimeout(interceptAllPrices, 500);
    });
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
